import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useProjectMeta } from "@/hooks/meta/useProjectMeta";
import { useCreateChapter } from "@/hooks/useChapters";
import { useDialog } from "@/hooks/useDialog";
// import { useProject } from "@/hooks/useProjects";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    BarChart,
    BookOpen,
    Layers,
    Plus,
    Timer,
    X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export function IntroCard() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (projectId) {
            const isClosed = localStorage.getItem(`intro_card_closed_${projectId}`);
            if (isClosed === 'true') {
                setIsVisible(false);
            }
        }
    }, [projectId]);

    const dismiss = () => {
        if (projectId) {
            localStorage.setItem(`intro_card_closed_${projectId}`, 'true');
            setIsVisible(false);
        }
    };

    const { openDialog } = useDialog();
    const { currentStoryId } = useProjectMeta(projectId || null);
    const createChapterMutation = useCreateChapter();

    const [activeId, setActiveId] = useState<string>('plan');

    if (!isVisible) return null;

    const modules = [
        {
            id: 'plan',
            title: 'Timeline',
            subtitle: "Architect your story",
            description: "The Blueprint. Use the Timeline to map out your key beats, manage chronology, and ensure your plot has no holes before you write a single word.",
            icon: Timer,
            href: `/projects/${projectId}/timeline`,
            tags: ["Non-linear plotting", "Beat sheets", "Structure"]
        },
        {
            id: 'build',
            title: 'World Entities',
            subtitle: "Build your bible",
            description: "The Context. A living database for your Characters, Settings, and Items. Keep your world consistent and reference details instantly while writing.",
            icon: Layers,
            href: `/projects/${projectId}/elements`,
            tags: ["Character profiles", "Locations", "Lore"]
        },
        {
            id: 'draft',
            title: 'Manuscript',
            subtitle: "Write the prose",
            description: "The Draft. A distraction-free environment integrated with your notes. Write your chapters with full access to your timeline and world details.",
            icon: BookOpen,
            href: `/projects/${projectId}/chapters`,
            action: 'create-chapter',
            tags: ["Distraction-free", "Integrated notes", "Export"]
        },
        {
            id: 'track',
            title: 'Progress',
            subtitle: "Track momentum",
            description: "The Pulse. Visualize your writing habits, set word count goals, and maintain your streak. Understanding your output is key to finishing.",
            icon: BarChart,
            href: `/projects/${projectId}/progress`,
            tags: ["Analytics", "Goals", "Habits"]
        }
    ];

    const activeModule = modules.find(m => m.id === activeId) || modules[0];

    const handleAction = async (module: any) => {
        if (module.action === 'create-chapter') {
            const storyId = currentStoryId;

            if (!storyId) {
                toast.error("No active story found to create a chapter in.");
                return;
            }

            openDialog('chapter', {
                defaultValues: { name: '', description: '', outline: '' }
            }, async (data: { name: string; description: string; outline?: string }) => {
                try {
                    const newChapter = await createChapterMutation.mutateAsync({
                        storyId: parseInt(storyId.toString()),
                        title: data.name,
                        description: data.description,
                        projectId: projectId!
                    });

                    toast.success("Chapter created successfully");

                    // Navigate to the chapter editor if created
                    if (newChapter && newChapter.id) {
                        navigate(`/projects/${projectId}/chapters/${newChapter.id}`);
                    }
                } catch (error) {
                    console.error("Failed to create chapter", error);
                    toast.error("Failed to create chapter");
                }
            });
        } else {
            navigate(module.href);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
        >
            <Card className="relative overflow-hidden border-border bg-card shadow-sm group/container">
                {/* Unified Glass Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background/50 pointer-events-none" />

                {/* Dismiss Button - Top Right */}
                <div className="absolute top-2 right-2 z-20">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => dismiss()}
                        className="h-8 w-8 text-muted-foreground/50 hover:text-foreground hover:bg-background/80 rounded-full transition-colors"
                        title="Dismiss guide"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <CardContent className="relative p-0 grid grid-cols-1 lg:grid-cols-12 min-h-[320px]">

                    {/* Left Column: Navigation List */}
                    <div className="lg:col-span-3 p-4 border-b lg:border-b-0 lg:border-r border-border/40 flex flex-col justify-between bg-muted/5">
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between mb-4 px-3 pt-2">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/50">
                                    Get Started
                                </h3>
                            </div>

                            <div className="space-y-1 flex-1">
                                {modules.map((module) => (
                                    <button
                                        key={module.id}
                                        onMouseEnter={() => setActiveId(module.id)}
                                        onClick={() => setActiveId(module.id)}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200 outline-none focus-visible:ring-2",
                                            activeId === module.id
                                                ? "bg-background shadow-sm ring-1 ring-border/50"
                                                : "hover:bg-background/40 text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-7 w-7 rounded-md flex items-center justify-center transition-colors shrink-0",
                                            activeId === module.id ? "bg-primary/10 text-primary" : "bg-muted/20 text-muted-foreground"
                                        )}>
                                            <module.icon className="h-3.5 w-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className={cn("font-medium text-sm truncate", activeId === module.id ? "text-foreground" : "text-muted-foreground")}>
                                                {module.title}
                                            </div>
                                        </div>
                                        {activeId === module.id && (
                                            <motion.div layoutId="active-indicator" className="ml-auto shrink-0">
                                                <ArrowRight className="h-3.5 w-3.5 text-primary" />
                                            </motion.div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Dynamic Preview */}
                    <div className="lg:col-span-9 p-8 relative flex flex-col justify-center lg:justify-start">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeModule.id}
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.2 }}
                                className="h-full flex flex-col justify-between"
                            >
                                <div className="space-y-4">
                                    <h2 className="text-3xl font-bold tracking-tight text-foreground">
                                        {activeModule.subtitle}
                                    </h2>
                                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
                                        {activeModule.description}
                                    </p>
                                </div>

                                <div className="pt-4">
                                    <Button
                                        onClick={() => handleAction(activeModule)}
                                        className="rounded-full px-6 h-10 gap-2 font-medium shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all"
                                    >
                                        {activeModule.action === 'create-chapter' ? (
                                            <>
                                                <Plus className="h-4 w-4" />
                                                Create Chapter
                                            </>
                                        ) : (
                                            <>
                                                Open {activeModule.title}
                                                <ArrowRight className="h-4 w-4" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Background Decoration */}
                        <div className="absolute right-0 top-0 p-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-50" />
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
